import { createSelector } from '@reduxjs/toolkit';
import moment from 'moment';
import { AppState } from '../types';
import { useSelector } from 'react-redux';
import { SentinelFeature } from '../../models/organization';

const authSelector = (state: AppState) => state.auth;

/** A selector which returns entire auth state. */
export const selectAuthState = createSelector(authSelector, state => state);

/** A selector which returns information about the currently authenticated user (from the portal system). */
export const selectAuthUserInfo = createSelector(authSelector, state => state.user);

/** A selector which returns information about the currently authenticated user's org. */
export const selectAuthUserOrg = createSelector(selectAuthUserInfo, user => user?.orgDetails);

/** A selector which returns when the auth token was issued. */
export const selectAuthTokenIssuedAt = createSelector(authSelector, state => state.issuedAt);

/** A selector which returns feature flag status from the auth user. */
export const selectUserFeatureFlags = createSelector(selectAuthUserOrg, userOrg => {
  const flagsRecord: Record<SentinelFeature, boolean> = {
    BUNDLES: false,
    LOGS_LIVE_STREAM: false,
  };

  userOrg?.features?.forEach(flag => {
    if (!flag.feature) {
      return;
    }

    const now = new Date();
    let flagIsOn = false;

    if (flag.startDate && flag.endDate) {
      flagIsOn = new Date(flag.startDate) < now && new Date(flag.endDate) > now;
    } else if (!flag.startDate && !flag.endDate) {
      flagIsOn = true; // If both dates are null, feature is "on"
    } else if (!flag.endDate) {
      flagIsOn = new Date(flag.startDate) < now;
    } else {
      flagIsOn = new Date(flag.endDate) > now;
    }

    flagsRecord[flag.feature] = flagIsOn;
  });

  return flagsRecord;
});

/** A selector which returns the list of controllers the user has access to. */
export const selectAuthUserControllers = createSelector(selectAuthUserInfo, info => info?.controllers);

/**
 * A selector which returns when the refresh token expires.
 * TODO: It would be better if we could somehow get the expiration duration from Cognito. Right now we assume the tokens last for 30 days, which is the default.
 */
export const selectAuthRefreshTokenExpiresAt = createSelector(selectAuthTokenIssuedAt, issuedAt => {
  if (issuedAt === undefined) return undefined;
  return moment(issuedAt).add(30, 'days').unix() * 1000;
});

export function useAuthUserInfo() {
  return useSelector(selectAuthUserInfo);
}
